import { BrowserAuthError, RedirectRequest } from '@azure/msal-browser';
import { msalInstance } from 'services/authentication/msalConfig';
import { logger } from 'services/logs/logger';

/**
 * Module scope on purpose: the guard is shared by the React tree (ProtectedRoute)
 * and the non-React api client path, and it survives re-renders.
 *
 * MSAL sets its `interaction.status` flag synchronously when loginRedirect is
 * called, but the browser only leaves the page a few hundred milliseconds later
 * (PKCE generation + B2C metadata discovery). Any second call inside that window
 * throws `interaction_in_progress`.
 */
let isRedirectInFlight = false;

/**
 * Triggers a login redirect at most once per page lifetime.
 *
 * Resolves to `false` only when the redirect genuinely could not be started
 * (typically `endpoints_resolution_error`, i.e. the B2C OpenID configuration
 * could not be fetched). Callers should surface an error instead of leaving the
 * user on a blank page, since no navigation is going to happen.
 */
export const requestLoginRedirect = async (request: RedirectRequest): Promise<boolean> => {
  if (isRedirectInFlight) {
    logger.logInfo('Login redirect already in flight, skipping duplicate request');
    return true;
  }

  isRedirectInFlight = true;

  try {
    // In the nominal case the browser navigates away before this resolves.
    await msalInstance.loginRedirect(request);
    return true;
  } catch (error) {
    if (error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress') {
      // An interaction started before this page render owns the flow, let it finish.
      // The flag stays raised: retrying would only collide with it again.
      logger.logInfo('Login redirect skipped: MSAL interaction already in progress');
      return true;
    }

    isRedirectInFlight = false;
    // MSAL discards the underlying cause of `endpoints_resolution_error`, so name
    // the authority to tell a sign-in failure from a password-reset one.
    logger.logError(
      `Error during login redirect to ${request.authority ?? 'the sign-in authority'}`,
      error,
    );

    return false;
  }
};
